import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoneyCents } from '@theme/utilities';
import { addToCart } from './cart-add-helper.js';

class ColorationPdp extends Component {
  requiredRefs = [
    'mediaSlides',
    'priceContainer',
    'onetimeRadio',
    'subscriptionRadio',
    'onetimeLabel',
    'subscriptionLabel',
    'onetimePrice',
    'subscriptionPrice',
    'subInfo',
    'variantIdInput',
    'sellingPlanInput',
    'addToCartButton',
    'addToCartError',
    'liveRegion',
  ];

  connectedCallback() {
    super.connectedCallback();

    this.#applyQuizOriginFlag();

    this.sellingPlanId = this.dataset.sellingPlanId;
    this.sellingPlanDiscount = parseInt(this.dataset.sellingPlanDiscount, 10);
    this.productId = this.dataset.productId;

    // Parse oxydant variant data
    const oxydantJson = this.querySelector('[data-oxydant-variants]');
    try {
      this.oxydantVariants = oxydantJson ? JSON.parse(oxydantJson.textContent) : [];
    } catch {
      this.oxydantVariants = [];
    }

    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener(ThemeEvents.variantSelected, (e) => this.handleVariantSelected(e));
      section.addEventListener(ThemeEvents.variantUpdate, (e) => this.handleVariantUpdate(e));
    }

    this.#initKitMode();
  }

  /* ── Quiz origin: unlock buy/subscribe UI when the user came from the color quiz ── */

  #applyQuizOriginFlag() {
    let fromQuiz = false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('kit') === '1') fromQuiz = true;
      if (!fromQuiz && sessionStorage.getItem('lv_from_color_quiz') === '1') fromQuiz = true;
    } catch { /* ignore */ }

    if (fromQuiz) {
      this.dataset.fromQuiz = 'true';
      // Mirror to <html> so sibling sections (e.g. the floating "Sticky CTA"
      // block) can be shown/hidden via CSS without coupling to this element.
      document.documentElement.dataset.fromColorQuiz = 'true';
    }
  }

  /* ── Kit mode (from quiz redirect) ── */

  #initKitMode() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('kit') !== '1') return;

    let kitData;
    try {
      kitData = JSON.parse(localStorage.getItem('cq_kit'));
      if (kitData?._ts && Date.now() - kitData._ts > 3600000) {
        localStorage.removeItem('cq_kit');
        kitData = null;
      }
    } catch { /* ignore */ }
    if (!kitData || !kitData.items?.length) return;

    this.kitMode = true;
    this.kitName = kitData.name || '';
    this.kitFormula = kitData.formula || '';
    this.kitVolume = kitData.vol || '20';
    this.kitItems = kitData.items; // [{variantId, handle, name, code, price, img}, ...]

    // ── Title: show shade composition ──
    if (this.refs.productTitle) {
      const kitTitle = this.kitItems.map((i) => i.name).join(' + ');
      // Keep badge, replace the text node
      this.refs.productTitle.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
      });
      this.refs.productTitle.insertAdjacentText('afterbegin', kitTitle + ' ');
    }
    if (this.refs.kitBadge) this.refs.kitBadge.hidden = false;

    // Subtitle: "pour Marie · formula"
    if (this.refs.kitSubtitle) {
      const parts = [];
      if (this.kitName) parts.push(`pour ${this.kitName}`);
      if (this.kitFormula) parts.push(this.kitFormula);
      if (parts.length) {
        this.refs.kitSubtitle.textContent = parts.join(' · ');
        this.refs.kitSubtitle.hidden = false;
      }
    }

    // Hide product description (doesn't apply to the kit)
    if (this.refs.productDescription) this.refs.productDescription.hidden = true;

    // ── Media: replace gallery with kit bundle visual ──
    if (this.refs.kitMedia && this.refs.kitMediaStack) {
      // Hide normal gallery, show kit media
      if (this.refs.mediaGallery) this.refs.mediaGallery.hidden = true;
      this.refs.kitMedia.hidden = false;

      // Build stacked product images
      this.kitItems.forEach((item) => {
        if (!item.img) return;
        const img = document.createElement('img');
        img.src = item.img;
        img.alt = item.name;
        img.width = 180;
        img.height = 180;
        img.loading = 'eager';
        this.refs.kitMediaStack.appendChild(img);
      });

      // Label
      if (this.refs.kitMediaLabel) {
        const count = this.kitItems.length;
        this.refs.kitMediaLabel.textContent = `${count} tube${count > 1 ? 's' : ''} + oxydant`;
      }
    }

    // ── Price: compute total from all kit items ──
    // Kit item prices are in dollars (e.g., 14.99), convert to cents for consistency
    this.kitTotalCents = Math.round(this.kitItems.reduce((sum, i) => sum + (i.price || 0) * 100, 0));
    this.#updateKitPrice();

    // Hide normal price and variant picker (kit determines the variants)
    this.refs.priceContainer.hidden = true;
    if (this.refs.kitPriceContainer) this.refs.kitPriceContainer.hidden = false;
    if (this.refs.variantPicker) this.refs.variantPicker.hidden = true;

    // Override purchase toggle prices with kit totals
    this.refs.onetimePrice.textContent = formatMoneyCents(this.kitTotalCents);
    const discountedTotal = Math.round(this.kitTotalCents * (1 - this.sellingPlanDiscount / 100));
    this.refs.subscriptionPrice.textContent = formatMoneyCents(discountedTotal);
    if (this.refs.subscriptionSavings) {
      const savingsLabel = this.dataset.savingsLabel || '';
      this.refs.subscriptionSavings.textContent = `${savingsLabel} ${formatMoneyCents(this.kitTotalCents - discountedTotal)}`;
    }

    // Hide quiz link
    if (this.refs.quizLink) this.refs.quizLink.hidden = true;

    // ── Oxydant: pre-select volume from formula ──
    if (this.refs.oxydantVolumeRadios) {
      this.refs.oxydantVolumeRadios.forEach((radio) => {
        const match = radio.value.match(/(\d+)/);
        radio.checked = !!(match && match[1] === this.kitVolume);
      });
      this.#updateOxydantPrice();
    }

    // Show quiz recommendation note under oxydant volume
    if (this.refs.oxydantQuizNote && this.kitVolume) {
      const template = this.refs.oxydantQuizNote.dataset.quizNoteTemplate || '';
      this.refs.oxydantQuizNote.textContent = template.replace('[vol]', this.kitVolume);
      this.refs.oxydantQuizNote.hidden = false;
    }

    // Ensure oxydant is checked
    if (this.refs.oxydantCheckbox && !this.refs.oxydantCheckbox.checked) {
      this.refs.oxydantCheckbox.checked = true;
      this.handleOxydantToggle();
    }
  }

  #updateKitPrice() {
    if (!this.kitMode) return;
    const isSubscription = this.refs.subscriptionRadio.checked;

    // Tubes total
    let total = this.kitTotalCents;

    // Add oxydant price if included
    const oxydantVariant = this.refs.oxydantCheckbox?.checked ? this.#getSelectedOxydantVariant() : null;
    if (oxydantVariant) total += oxydantVariant.price;

    // Apply subscription discount
    const displayPrice = isSubscription
      ? Math.round(total * (1 - this.sellingPlanDiscount / 100))
      : total;

    if (this.refs.kitPriceTotal) {
      this.refs.kitPriceTotal.textContent = formatMoneyCents(displayPrice);
    }
    if (this.refs.kitPriceDetail) {
      const tubeCount = this.kitItems.length;
      const parts = [`${tubeCount} tube${tubeCount > 1 ? 's' : ''}`];
      if (oxydantVariant) parts.push('oxydant');
      this.refs.kitPriceDetail.textContent = parts.join(' + ') + (isSubscription ? ' · abonnement' : '');
    }
  }

  /* ── Gallery ── */

  selectMedia({ index }) {
    const slides = this.refs.mediaSlides;
    slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });

    this.refs.thumbnailButtons?.forEach((thumb, i) => {
      thumb.classList.toggle('coloration-pdp__thumb--active', i === index);
      thumb.setAttribute('aria-pressed', String(i === index));
    });
  }

  #navigateToMedia(mediaId) {
    if (!mediaId) return;
    const slides = this.refs.mediaSlides;
    const idx = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (idx !== -1) {
      this.selectMedia({ index: idx });
    }
  }

  /* ── Variant events ── */

  handleVariantSelected() {
    if (this.kitMode) return; // kit mode handles its own state
    this.refs.addToCartButton.setAttribute('disabled', '');
    this.refs.addToCartButton.textContent = this.dataset.addingText || '...';
  }

  handleVariantUpdate(event) {
    if (this.kitMode) return; // kit mode ignores variant picker events

    const { resource, data } = event.detail;

    // Update variant ID hidden input
    if (resource) {
      this.refs.variantIdInput.value = resource.id;
    }

    // Update price from section rendering API
    if (data?.html) {
      const newPrice = data.html.getElementById(`price-${this.dataset.sectionId}`);
      if (newPrice) {
        this.refs.priceContainer.innerHTML = newPrice.innerHTML;
      }
    }

    // Update button state
    if (!resource || !resource.available) {
      this.refs.addToCartButton.setAttribute('disabled', '');
      this.refs.addToCartButton.textContent = this.dataset.soldOutText;
    } else {
      this.refs.addToCartButton.removeAttribute('disabled');
      this.refs.addToCartButton.textContent = this.dataset.addToCartText;
    }

    // Update subscription price display
    this.#updateSubscriptionPrice(resource);

    // Navigate to variant media
    if (resource?.featured_media) {
      this.#navigateToMedia(resource.featured_media.id);
    }
  }

  #updateSubscriptionPrice(variant) {
    if (!variant) return;
    const price = variant.price;
    const discountedPrice = Math.round(price * (1 - this.sellingPlanDiscount / 100));
    this.refs.subscriptionPrice.textContent = formatMoneyCents(discountedPrice);
    this.refs.onetimePrice.textContent = formatMoneyCents(price);

    if (this.refs.subscriptionSavings) {
      const savings = price - discountedPrice;
      const savingsLabel = this.dataset.savingsLabel || '';
      this.refs.subscriptionSavings.textContent = `${savingsLabel} ${formatMoneyCents(savings)}`;
    }
  }

  /* ── Purchase type toggle ── */

  handlePurchaseTypeChange() {
    const isSubscription = this.refs.subscriptionRadio.checked;

    this.refs.onetimeLabel.classList.toggle('coloration-pdp__purchase-card--active', !isSubscription);
    this.refs.subscriptionLabel.classList.toggle('coloration-pdp__purchase-card--active', isSubscription);
    this.refs.subInfo.hidden = !isSubscription;

    this.refs.sellingPlanInput.value = isSubscription ? this.sellingPlanId : '';

    if (this.kitMode) {
      // Recalculate kit total with subscription toggle
      const total = this.kitTotalCents;
      const discountedTotal = Math.round(total * (1 - this.sellingPlanDiscount / 100));
      this.refs.onetimePrice.textContent = formatMoneyCents(total);
      this.refs.subscriptionPrice.textContent = formatMoneyCents(discountedTotal);
      this.#updateKitPrice();
    }

    this.#updateOxydantPrice();
  }

  /* ── Oxydant toggle ── */

  handleOxydantToggle() {
    if (!this.refs.oxydantCheckbox) return;
    const include = this.refs.oxydantCheckbox.checked;
    if (this.refs.oxydantDismissCheckbox) this.refs.oxydantDismissCheckbox.checked = !include;
    if (this.refs.oxydantBody) this.refs.oxydantBody.hidden = !include;
    if (this.kitMode) this.#updateKitPrice();
  }

  handleOxydantDismiss() {
    if (!this.refs.oxydantDismissCheckbox) return;
    const dismissed = this.refs.oxydantDismissCheckbox.checked;
    if (this.refs.oxydantCheckbox) this.refs.oxydantCheckbox.checked = !dismissed;
    if (this.refs.oxydantBody) this.refs.oxydantBody.hidden = dismissed;
    if (this.kitMode) this.#updateKitPrice();
  }

  handleOxydantSizeChange() {
    this.#updateOxydantPrice();
    if (this.kitMode) this.#updateKitPrice();
  }

  handleOxydantVolumeChange() {
    this.#updateOxydantPrice();
    if (this.kitMode) this.#updateKitPrice();
  }

  #getSelectedOxydantVariant() {
    if (!this.refs.oxydantSizeRadios || !this.refs.oxydantVolumeRadios) return null;
    const sizeRadio = this.refs.oxydantSizeRadios.find((r) => r.checked);
    const volumeRadio = this.refs.oxydantVolumeRadios.find((r) => r.checked);
    if (!sizeRadio || !volumeRadio) return null;

    return this.oxydantVariants.find(
      (v) => v.option1 === volumeRadio.value && v.option2 === sizeRadio.value
    ) || null;
  }

  #updateOxydantPrice() {
    const variant = this.#getSelectedOxydantVariant();
    if (variant && this.refs.oxydantPrice) {
      const isSubscription = this.refs.subscriptionRadio.checked;
      const price = (isSubscription && variant.sellingPlanId)
        ? Math.round(variant.price * (1 - this.sellingPlanDiscount / 100))
        : variant.price;
      this.refs.oxydantPrice.textContent = formatMoneyCents(price);
    }
  }

  /* ── Add to cart ── */

  async handleAddToCart(event) {
    event.preventDefault();

    const isSubscription = this.refs.subscriptionRadio.checked;
    const sellingPlan = isSubscription ? this.sellingPlanId : '';
    const formData = new FormData();

    if (this.kitMode) {
      // ── Kit mode: add all tube items + oxydant ──
      const includeOxydant = this.refs.oxydantCheckbox?.checked;
      const oxydantVariant = includeOxydant ? this.#getSelectedOxydantVariant() : null;
      const bundleKey = `coloration-kit-${Date.now()}`;
      const bundleTitle = isSubscription
        ? (this.dataset.bundleTitleSubscription || 'Coloration Pro - Kit (Abonnement)')
        : (this.dataset.bundleTitleOnetime || 'Kit Coloration Pro');

      // Add each tube product
      this.kitItems.forEach((item, idx) => {
        formData.append(`items[${idx}][id]`, item.variantId);
        formData.append(`items[${idx}][quantity]`, '1');
        if (sellingPlan) formData.append(`items[${idx}][selling_plan]`, sellingPlan);
        formData.append(`items[${idx}][properties][_bundle_key]`, bundleKey);
        formData.append(`items[${idx}][properties][_bundle_title]`, bundleTitle);
        formData.append(`items[${idx}][properties][_bundle_parent]`, idx === 0 ? 'true' : '');
        if (this.kitName) formData.append(`items[${idx}][properties][Formule pour]`, this.kitName);
        if (this.kitFormula) formData.append(`items[${idx}][properties][Formule]`, this.kitFormula);
      });

      // Add oxydant
      if (oxydantVariant) {
        const oIdx = this.kitItems.length;
        formData.append(`items[${oIdx}][id]`, oxydantVariant.id);
        formData.append(`items[${oIdx}][quantity]`, '1');
        if (sellingPlan && oxydantVariant.sellingPlanId) {
          formData.append(`items[${oIdx}][selling_plan]`, oxydantVariant.sellingPlanId);
        }
        formData.append(`items[${oIdx}][properties][_bundle_key]`, bundleKey);
        formData.append(`items[${oIdx}][properties][_bundle_title]`, bundleTitle);
        formData.append(`items[${oIdx}][properties][_bundle_parent]`, '');
        if (this.kitName) formData.append(`items[${oIdx}][properties][Formule pour]`, this.kitName);
        if (this.kitFormula) formData.append(`items[${oIdx}][properties][Formule]`, this.kitFormula);
      }

      // Add promo bundle (free gift from quiz)
      const promoBundleVariant = this.dataset.promoBundleVariant || '51961719226670';
      if (promoBundleVariant) {
        const pIdx = this.kitItems.length + (oxydantVariant ? 1 : 0);
        formData.append(`items[${pIdx}][id]`, promoBundleVariant);
        formData.append(`items[${pIdx}][quantity]`, '1');
        formData.append(`items[${pIdx}][properties][_bundle_key]`, bundleKey);
        formData.append(`items[${pIdx}][properties][_bundle_title]`, bundleTitle);
        formData.append(`items[${pIdx}][properties][_bundle_parent]`, '');
        formData.append(`items[${pIdx}][properties][_quiz_coloration]`, 'true');
      }
    } else {
      // ── Normal mode: single product + optional oxydant ──
      const variantId = this.refs.variantIdInput.value;
      const includeOxydant = this.refs.oxydantCheckbox?.checked;
      const oxydantVariant = includeOxydant ? this.#getSelectedOxydantVariant() : null;
      const useBundle = includeOxydant && oxydantVariant;
      const bundleKey = useBundle ? `coloration-pdp-${Date.now()}` : null;
      const bundleTitle = useBundle
        ? (isSubscription
            ? (this.dataset.bundleTitleSubscription || 'Coloration Pro - Kit (Abonnement)')
            : (this.dataset.bundleTitleOnetime || 'Kit Coloration Pro'))
        : null;

      formData.append('items[0][id]', variantId);
      formData.append('items[0][quantity]', '1');
      if (sellingPlan) formData.append('items[0][selling_plan]', sellingPlan);
      if (bundleKey) {
        formData.append('items[0][properties][_bundle_key]', bundleKey);
        formData.append('items[0][properties][_bundle_title]', bundleTitle);
        formData.append('items[0][properties][_bundle_parent]', 'true');
      }

      if (oxydantVariant) {
        formData.append('items[1][id]', oxydantVariant.id);
        formData.append('items[1][quantity]', '1');
        if (sellingPlan && oxydantVariant.sellingPlanId) {
          formData.append('items[1][selling_plan]', oxydantVariant.sellingPlanId);
        }
        if (bundleKey) {
          formData.append('items[1][properties][_bundle_key]', bundleKey);
          formData.append('items[1][properties][_bundle_title]', bundleTitle);
          formData.append('items[1][properties][_bundle_parent]', '');
        }
      }
    }

    const kitMode = this.kitMode;
    await addToCart({
      formData,
      button: this.refs.addToCartButton,
      errorContainer: this.refs.addToCartError,
      liveRegion: this.refs.liveRegion,
      addToCartText: this.dataset.addToCartText,
      addingText: this.dataset.addingText || '...',
      addedText: this.dataset.addedText || '',
      successClass: 'coloration-pdp__btn--success',
      onSuccess: () => {
        if (kitMode) {
          try { localStorage.removeItem('cq_kit'); } catch { /* ignore */ }
        }
      },
    });
  }
}

customElements.define('coloration-pdp', ColorationPdp);
